import { globals } from 'config/globals';
import { HomeParticle } from 'app/views/home/webgl/particles/fields/HomeParticle';
import { LookupType, RepellType } from 'app/enum/Particles';
import { particles } from 'app/contracts/Particles';
import ComboCounter from 'utils/ComboCounter';

export default class HomeMicroShape {
	private config: any = {
		w: 1,
		h: 4,
		p: [
			{ x: 0, y: 0 },
			{ x: 1, y: 0 },
			{ x: 2, y: 0 },
			{ x: 3, y: 0 }
		]
	};

	private active: boolean = true;
	private gridSize: number;
	private x: number;
	private y: number;
	private scale: number;
	private alpha: number = 0;
	private pool: Array<HomeParticle>;
	private points: Array<MircoShapePoint> = [];
	private activePoints: Array<MircoShapePoint> = [];
	private nonActivePoints: Array<MircoShapePoint> = [];
	private _counters: ComboCounter;

	//Shared scene-state bag (threaded down from ScreensDepartment via
	//HomeMicroShapeController) — read for the virtual-canvas ratio (ex
	//globals.virtualWidth/virtualHeight):
	private _sceneProps: particles.SceneProps;

	constructor(config: any, sceneProps: particles.SceneProps) {
		this.config = config;
		this._sceneProps = sceneProps;
		this._counters = new ComboCounter();
	}

	assignPool(particlePool: Array<HomeParticle>, fromIndex: number): number {
		this.pool = [];
		let i = 0;
		let blockParticleTotal = 9;
		for (i = fromIndex; i < fromIndex + blockParticleTotal; i++) {
			if (particlePool[i]) {
				this.pool.push(particlePool[i]);
			}
		}

		return i;
	}

	init() {
		this.assignShapePool();
		this.activatePoints();
		this.assignConfig(this.config);
		this._counters.clear();

		this._counters.add(
			() => {
				if (this.active) {
					this.startMove();
					this.startBreathing();
					this.startWhoozyRepell();
				}
			},
			1000 + Math.random() * 16000
		);
	}

	public reset() {
		this.active = false;
		this._counters.clear();
		for (var i = 0; i < this.points.length; i++) {
			this.points[i].particle.animatedProps.stopBreathing();
		}
	}

	startMove() {
		if (!this.active) {
			return;
		}

		Math.random() < 0.5 ? -1 : 1;
		Math.random() < 0.5 ? -1 : 1;

		this._counters.add(
			() => {
				if (this.active) {
					this.startMove();
				}
			},
			1000 + Math.random() * 2000
		);
	}

	switchShape(shapeConfig: any, colour: string = null) {
		if (!this.active) {
			return;
		}

		this.assignConfig(shapeConfig);
		this.hideNonActive();
		this.setScale();
		this.setAlpha();
		this.positionParticles();

		if (colour) {
			this.setColour(colour);
		}
	}

	assignShapePool() {
		const rand = Math.random();
		let type = 'solid2.png';
		if (rand < 0.05) type = 'solid-tri.png';
		else if (rand < 0.1) type = 'solid-ring.png';

		for (var i = 0; i < this.pool.length; i++) {
			var point = new MircoShapePoint();
			point.particle = this.pool[i];
			point.particle.changeParticle(type);
			if (point.particle) {
				this.points.push(point);
			}
		}
	}

	activatePoints() {
		for (var i = 0; i < this.points.length; i++) {
			if (this.points[i].particle) {
				this.points[i].particle.activate();
				this.points[i].particle.deactivateAllBehaviours(true);
				this.points[i].particle.animatedProps.stopBreathing();
				this.points[i].particle.orbitPath.active = false;

				this.points[i].particle.animatedProps.alpha = this.points[i].particle.animatedProps.targetAlpha = 0;

				this.points[i].particle.animatedProps.setEase({
					x: 20,
					y: 20,
					scale: 10,
					alpha: 2
				});

				this.points[i].particle.repell.active = !globals.screensMobileLayout;
				this.points[i].particle.repell.type = RepellType.PUSH;
				this.points[i].particle.repell.radius = 100;
				this.points[i].particle.repell.randomisePower(30, 200);

				this.points[i].particle.animatedProps._x.setVectorConfig(false);
				this.points[i].particle.animatedProps._y.setVectorConfig(false);
			}
		}
	}

	assignConfig(config: any) {
		this.config = config;
		this.activePoints = [];
		this.nonActivePoints = [];

		for (var i = 0; i < this.points.length; i++) {
			if (this.config.p[i]) {
				this.points[i].configX = this.config.p[i].x;
				this.points[i].configY = this.config.p[i].y;
				this.points[i].active = true;
				this.activePoints.push(this.points[i]);
			} else {
				this.points[i].active = false;
				this.nonActivePoints.push(this.points[i]);
			}
		}
	}

	fadeIn() {
		this.alpha = 1;
		this.setAlpha();
	}

	//NOTE: the `i` param is shadowed by the hoisted `var i` loops in the dead code
	//below the first return — annotated number so the declarations agree:
	orbit(i: number, shapeConfig: any) {
		this.switchShape(shapeConfig);

		return;
		for (var i = 0; i < this.points.length; i++) {
			this.points[i].particle.orbitPath.active = true;
			this.points[i].particle.orbitPath.speed = 1;
			this.points[i].particle.orbitPath.speedMuiliplier = 1;
			this.points[i].particle.orbitPath.xMuliplier = 0;
			this.points[i].particle.orbitPath.yMuliplier = 10;
			this.points[i].particle.orbitPath.pathIndex = Math.round(
				((this.points[i].particle.particleProps.sprite.x + 1000) / 1200) * 360
			);
			this.points[i].particle.orbitPath.path = LookupType.SINE;

		}

		return;
		let dx = Math.random() < 0.5 ? -1 : 1;
		let dy = Math.random() < 0.5 ? -1 : 1;
		let x = Math.random() < 0.5;

		if (Math.random() < 0.4) {
			if (x) {
				this.x += 0.004 * dx;
			} else {
				this.y += 0.02 * dy;
			}

			for (var i = 0; i < this.points.length; i++) {
				this.points[i].particle.animatedProps._x.setVectorConfig(true);
				this.points[i].particle.animatedProps._y.setVectorConfig(true);

				this.scale = 0.15 + Math.random() * 0.15;
				this.setScale();
			}

			setTimeout(
				() => {
					let dx = Math.random() < 0.5 ? -1 : 1;
					let dy = Math.random() < 0.5 ? -1 : 1;
					let x = Math.random() < 0.5;

					if (Math.random() < 0.4) {
						if (x) {
							this.x += 0.01 * dx;
						} else {
							this.y += 0.05 * dy;
						}

						for (var i = 0; i < this.points.length; i++) {
							this.points[i].particle.animatedProps._x.setVectorConfig(true);
							this.points[i].particle.animatedProps._y.setVectorConfig(true);

							this.scale = 0.15 + Math.random() * 0.15;
							this.setScale();
						}
					}
				},
				1000 + (Math.random() + 500)
			);

			setTimeout(
				() => {
					let dx = Math.random() < 0.5 ? -1 : 1;
					let dy = Math.random() < 0.5 ? -1 : 1;
					let x = Math.random() < 0.5;

					if (Math.random() < 0.5) {
						if (x) {
							this.x += 0.01 * dx;
						} else {
							this.y += 0.05 * dy;
						}

						for (var i = 0; i < this.points.length; i++) {
							this.points[i].particle.animatedProps._x.setVectorConfig(true);
							this.points[i].particle.animatedProps._y.setVectorConfig(true);

							this.scale = 0.15 + Math.random() * 0.15;
							this.setScale();
						}
					}
				},
				2000 + (Math.random() + 500)
			);
		}

	}

	setShapeSize(x: number, y: number, gridSize: number, scale: number = 0.5) {
		this.x = x;
		this.y = y;
		this.gridSize = gridSize;
		this.scale = scale;
		this.positionParticles(true);
		this.setScale();
		this.setAlpha();
	}

	setColour(colour: string) {
		for (var i = 0; i < this.points.length; i++) {
			this.points[i].setColour(colour);
		}
	}

	startBreathing() {
		for (var i = 0; i < this.activePoints.length; i++) {
			//(A second `true` used to be passed — presumably a hardReset flag by
			//analogy with setScale/setAlpha — but MircoShapePoint.startBreathing
			//only takes scale, so JS always dropped it. Removed, no behaviour
			//change; add a real hardReset param if that reset is actually wanted.)
			this.points[i].startBreathing(this.scale);
		}
	}

	startWhoozyRepell() {
		for (var i = 0; i < this.activePoints.length; i++) {
			this.points[i].startWhoozyRepell();
		}
	}

	setScale() {
		for (var i = 0; i < this.activePoints.length; i++) {
			this.points[i].setScale(this.scale, true);
		}
	}

	setAlpha() {
		for (var i = 0; i < this.activePoints.length; i++) {
			this.points[i].setAlpha(this.alpha);
		}
	}

	tick() {
		this._counters.tick();
		this.positionParticles();
	}

	positionParticles(hardReset: boolean = false) {
		//| - | - | - | - |
		//0  0.125  0.25

		var gridX = this.gridSize;
		//Ex globals.virtualWidth / globals.virtualHeight — same metrics off
		//sceneProps.sizes (container.w/h = virtual width/height):
		var gridY = this.gridSize * (this._sceneProps.sizes.container.w / this._sceneProps.sizes.container.h);



		var indentX = gridX / 2;
		var indentY = gridY / 2;
		var cellX = (gridX * this.config.w - indentX) / this.config.w;
		var cellY = (gridY * this.config.h - indentY) / this.config.h;

		for (var i = 0; i < this.activePoints.length; i++) {
			this.activePoints[i].setPosition(
				this.x + indentX + cellX * (this.points[i].configX - 1),
				this.y + indentY + cellY * (this.points[i].configY - 1),
				hardReset
			);
			this.activePoints[i].particle.repell.active = !globals.screensMobileLayout;
		}

		for (var i = 0; i < this.nonActivePoints.length; i++) {
			this.nonActivePoints[i].setPosition(this.x, this.y, hardReset);
		}
	}

	hideNonActive() {
		for (var i = 0; i < this.nonActivePoints.length; i++) {
			this.nonActivePoints[i].setScale(0);
			this.nonActivePoints[i].particle.repell.active = false;
		}
	}

	move(x, y) {
		for (var i = 0; i < this.points.length; i++) {
			this.points[i].particle.animatedProps.targetX = x;
			this.points[i].particle.animatedProps.targetY = y;
			this.points[i].particle.repell.active = false;
		}
	}
}

class MircoShapePoint {
	public particle: HomeParticle;
	public x: number;
	public y: number;
	public configX: number;
	public configY: number;
	public active: boolean;

	setPosition(x: number, y: number, hardReset: boolean = false) {
		this.particle.position.set({ x: x, y: y }, hardReset);

		this.particle.animatedProps.rotation = this.particle.animatedProps.targetRotation = 0;
		this.particle.particleProps.sprite.rotation = 0;
		this.particle.orbitPath.syncRotation = false;
	}

	startBreathing(scale: number) {
		this.particle.animatedProps.startBreathing(scale, 0.02, 0.7);
		this.particle.particleProps.sprite.blendMode = 'add';
	}

	setScale(scale: number, hardReset: boolean = false) {
		this.particle.animatedProps.targetScale = this.particle.animatedProps.targetScaleSaved = scale;
		if (hardReset) this.particle.animatedProps.scale = scale;
	}

	startWhoozyRepell() {
		this.particle.repell.active = !globals.screensMobileLayout;
		this.particle.repell.type = RepellType.PUSH;
		this.particle.repell.radius = 300;

		this.particle.animatedProps._x.setVectorConfig(true, 0.2, 2);
		this.particle.animatedProps._y.setVectorConfig(true, 0.2, 2);
	}

	setAlpha(alpha: number, hardReset: boolean = false) {
		this.particle.animatedProps.targetAlpha = alpha;
		if (hardReset) this.particle.animatedProps.alpha = alpha;
	}

	setColour(colour: string) {
		this.particle.colour.set(colour);
	}
}
